// StructureBulkModal — bulk Add/Remove from structure for the selected people.
// Structure nodes are nested and can be many, so this uses a searchable indented
// node list (the same tree pattern as AddPeople/Invite) rather than chips. Level 0
// (company root) is a static header; deeper nodes are selectable. Returns the
// chosen node ids to PeoplePage, which POSTs company-users/bulk-action.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Icon, Input, Modal } from '@/components/common';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import type { OrgUnit } from '@/mocks/structure';

interface StructureBulkModalProps {
  open: boolean;
  variant: 'add' | 'remove';
  peopleCount: number;
  tree: OrgUnit[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (nodeIds: string[]) => void;
}

/** Flatten the org tree to rows carrying their depth for indentation. */
const flattenTree = (units: OrgUnit[], depth = 0): { unit: OrgUnit; depth: number }[] =>
  units.flatMap((unit) => [
    { unit, depth },
    ...(unit.children ? flattenTree(unit.children, depth + 1) : []),
  ]);

const StructureBulkModal = ({
  open,
  variant,
  peopleCount,
  tree,
  loading,
  onClose,
  onConfirm,
}: StructureBulkModalProps) => {
  const { t } = useTranslation();
  const levelConfig = useLevelConfig();
  const add = variant === 'add';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const all = flattenTree(tree);
    const q = search.trim().toLowerCase();
    return q ? all.filter(({ unit }) => unit.name.toLowerCase().includes(q)) : all;
  }, [tree, search]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    if (!selected.size || loading) return;
    onConfirm([...selected]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      tone='brand'
      icon={add ? 'network' : 'unlink'}
      title={t(add ? 'people_bulk_add_structure_title' : 'people_bulk_remove_structure_title')}
      subtitle={t(
        add ? 'people_bulk_add_structure_subtitle' : 'people_bulk_remove_structure_subtitle',
      )}
      footer={
        <>
          <Button variant='secondary' onClick={onClose}>
            {t('button_cancel')}
          </Button>
          <Button onClick={submit} disabled={!selected.size} loading={loading}>
            {t('button_continue')}
          </Button>
        </>
      }
    >
      <Chip variant='neutral' iconStart='users' className='people-bulk__count'>
        {t('people_bulk_people_selected', { count: peopleCount })}
      </Chip>

      <div className='people-bulk__search'>
        <Input
          size='sm'
          iconStart='search'
          placeholder={t('people_bulk_node_search_ph')}
          aria-label={t('people_bulk_node_search_ph')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {rows.length === 0 ? (
        <p className='people-bulk__empty'>{t('people_bulk_pick_empty')}</p>
      ) : (
        <div className='add-person__tree' role='group' aria-label={t('people_bulk_node_search_ph')}>
          {rows.map(({ unit, depth }) => {
            const indent = {
              paddingInlineStart: `calc(var(--space-3) + ${depth} * var(--space-5))`,
            };
            if (unit.level === 0) {
              return (
                <div
                  key={unit.id}
                  className='add-person__tree-row add-person__tree-row--static'
                  style={indent}
                >
                  <Icon
                    name={levelConfig(unit.level).icon}
                    size={16}
                    className='add-person__tree-icon'
                  />
                  <span className='add-person__tree-name'>{unit.name}</span>
                </div>
              );
            }
            const on = selected.has(unit.id);
            return (
              <button
                key={unit.id}
                type='button'
                className={`add-person__tree-row${on ? ' is-selected' : ''}`}
                aria-pressed={on}
                style={indent}
                onClick={() => toggle(unit.id)}
              >
                <Icon
                  name={levelConfig(unit.level).icon}
                  size={16}
                  className='add-person__tree-icon'
                />
                <span className='add-person__tree-name'>{unit.name}</span>
                {on && <Icon name='check' size={16} className='add-person__tree-check' />}
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default StructureBulkModal;
